import { AttachMoney, Close,  ExpandMore, SwapHoriz, ErrorOutline, Check, FilterAlt, HourglassEmpty } from '@mui/icons-material';
import { Box, Chip, Collapse, Divider, Link, Stack, ToggleButton, ToggleButtonGroup, TextField } from "@mui/material";
import { GridActionsCellItem, GridFilterInputValueProps, GridFilterItem, GridRowParams } from "@mui/x-data-grid-pro";
import {  GridFilterOperator } from "@mui/x-data-grid-pro";
import { sum } from "lodash";
import moment from "moment";
import { enqueueSnackbar } from "notistack";
import { Fragment, useMemo, useRef, useState } from "react";
import { Alert, DatePicker, Grid, Button, Loader, Modal, MultipleAutocomplete, MultipleAutocompleteOption, Paper, Table, Typography, DateRangePicker } from "../../../components";
import { useAxios, useToggle } from "../../../hooks";
import { composeIncomeSummary, formatDate, formatMoney, getPaymentStatusColor, getPaymentStatusLabel } from "../../../utils";

interface IncomeItem {
    amount: number;
    status: string;
    due_date: Date;
    order: any;
}

interface ClientSummary {
    name: string;
    total: number;
    pending: number;
    late: number;
    items: IncomeItem[];
}

const today = moment().toDate()
const oneMonth = moment().add(30, 'days').toDate()

const diff = moment(oneMonth).diff(today, 'days')

const month: Array<Date> = []

for (let day = 0; day < diff; day++) {
    month.push(moment(today).add(day, 'day').toDate())
}

const statusOptions = [
    { value: 'CANCELLED', label: 'Cancelado' },
    { value: 'PENDING', label: 'Pendente' },
    { value: 'PAID', label: 'Pago' },
    { value: 'LATE', label: 'Atrasado' },
]

function StatusSelector(props: GridFilterInputValueProps) {
    const { item, applyValue } = props;
  
    return (
        <MultipleAutocomplete
        variant="standard"
            label="Status"
            options={statusOptions}
            value={statusOptions.filter(option => item?.value?.includes(option.value))}
            onChange={(value) => {
                applyValue({
                    ...item,
                    value: (value as unknown as MultipleAutocompleteOption[]).map(item => item.value)
                })
            }}
        />
    );
  }
  
const statusSelectorOperator: GridFilterOperator[] = [
{
    label: 'é igual a',
    value: 'in',
    getValueAsString: (values) => {
        return values.map((item: string) => statusOptions.find(({value}) => value === item)?.label).join(', ')
    },
    getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (!Array.isArray(filterItem.value) || filterItem.value.length === 0 || !filterItem.value) {
            return null;
        }

        return ({ value }) => {
            return filterItem.value.includes(value)
        };
    },
    InputComponent: StatusSelector,
}]; 

export function Incomes () {
    const [modalVisibility, modalVisibilityControls] = useToggle(false);
    const [filterModalVisibility, filterModalVisibilityControls] = useToggle(false);
    const [pendingAction, setPendingAction] = useState<null | "CANCELLED" | "PAID">(null);
    const [selectedIncomeId, setSelectedIncomeId] = useState<number | null>(null);
    const [viewMode, setViewMode] = useState<'timeline' | 'clients'>('timeline');
    const [incomes, refetchIncomes] = useAxios(`/income`)

    const [api, callAPI] = useAxios({}, {manual: true})
    const [confirmModalVisible, setConfirmModalVisibility] = useToggle(false)
    const confirmModalAction = useRef<null | (() => Promise<void>)>(null)
    const [rowSelectionModel, setRowSelectionModel] = useState<Array<string | number>>([]);
    const [expandedClients, setExpandedClients] = useState<Record<string, boolean>>({});
    const [tableFilters, setTableFilters] = useState<{
        clientName: string;
        status: string[];
    }>({
        clientName: '',
        status: []
    });
    const [filters, setFilters] = useState<{
        clientName: string;
        dateRange: {
            start: moment.Moment | null;
            end: moment.Moment | null;
        };
        status: string[];
    }>({
        clientName: '',
        dateRange: {
            start: moment().startOf('month'),
            end: moment().endOf('month')
        },
        status: []
    });

    const [tempFilters, setTempFilters] = useState(filters);

    const [, updateIncome] = useAxios({
        url: `/income`,
        method: 'PUT'
    }, {
        manual: true
    })

    const handleActionClick = (action: "CANCELLED" | "PAID", incomeId: number) => {
        setPendingAction(action);
        setSelectedIncomeId(incomeId);
        modalVisibilityControls.setTrue(); 
    };

    const confirmAction = async () => {
        if (!selectedIncomeId || !pendingAction) return;

        const { status } = await updateIncome({
            url: `/income/${selectedIncomeId}`,
            method: 'PUT',
            data: { status: pendingAction }
        });

        if (status === 204) {
            enqueueSnackbar('Status atualizado com sucesso!', { variant: 'success' });
        }

        modalVisibilityControls.setFalse();
        setPendingAction(null);
        setSelectedIncomeId(null);
        window.location.reload()
    };

    const dash = useMemo(() => {
        return month.map(item => {
            const items = (incomes.data || [])
                ?.filter((income: { due_date: Date }) => moment(income.due_date).isSame(item, 'date'))
                ?.filter((income: { status: string }) => income.status !== 'CANCELLED')
                ?.map((item: { amount: string }) => Number(item.amount))

            return {
                due_date: formatDate(item),
                amount: sum(items)
            }
        })
    }, [incomes.data])

    const clientSummary = useMemo(() => {
        if (!incomes.data) return [] as ClientSummary[];
        
        const summary = (incomes.data || []).reduce((acc: any, income: any) => {
            const clientName = income.order?.customer?.name || 'Cliente não identificado';
            const incomeDate = moment(income.due_date).format('YYYY-MM-DD');

            if (!acc[clientName]) {
                acc[clientName] = {
                    total: 0,
                    pending: 0,
                    late: 0,
                    items: {}
                };
            }

            if (!acc[clientName].items[incomeDate]) {
                acc[clientName].items[incomeDate] = {
                    amount: 0,
                    status: income.status,
                    due_date: income.due_date,
                    order: income.order
                };
            }
            
            acc[clientName].total += Number(income.amount);
            if (income.status === 'PENDING') acc[clientName].pending += Number(income.amount);
            if (income.status === 'LATE') acc[clientName].late += Number(income.amount);
            
            acc[clientName].items[incomeDate].amount += Number(income.amount);
            
            return acc;
        }, {});

        return Object.entries(summary).map(([name, data]: [string, any]) => ({
            name,
            total: data.total,
            pending: data.pending,
            late: data.late,
            items: Object.values(data.items) as IncomeItem[]
        })) as ClientSummary[];
    }, [incomes.data]);

    const handleFilterChange = (field: string, value: any) => {
        setTempFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleApplyFilters = () => {
        setFilters(tempFilters);
        setTableFilters({
            clientName: '',
            status: []
        });
        filterModalVisibilityControls.setFalse();
    };

    const handleOpenFilterModal = () => {
        if (!filters.dateRange.start && !filters.dateRange.end) {
            const newFilters = {
                ...filters,
                dateRange: {
                    start: moment().startOf('month'),
                    end: moment().endOf('month')
                }
            };
            setTempFilters(newFilters);
            setFilters(newFilters);
        } else {
            setTempFilters(filters);
        }
        filterModalVisibilityControls.setTrue();
    };

    const handleClearFilters = () => {
        const clearedFilters = {
            clientName: '',
            dateRange: {
                start: moment().startOf('month'),
                end: moment().endOf('month')
            },
            status: []
        };
        setFilters(clearedFilters);
        setTempFilters(clearedFilters);
        setTableFilters({
            clientName: '',
            status: []
        });
    };

    const handleStatusClick = (clientName: string, status: string) => {
        setTableFilters({
            clientName,
            status: [status]
        });
    };

    const handleTotalClick = (clientName: string) => {
        setTableFilters({
            clientName,
            status: []
        });
    };

    const filteredTableData = useMemo(() => {
        let filtered = incomes?.data || [];
        
        if (filters.clientName) {
            filtered = filtered.filter((item: any) => 
                item.order?.customer?.name.toLowerCase().includes(filters.clientName.toLowerCase())
            );
        }

        if (filters.status.length > 0) {
            filtered = filtered.filter((item: any) => 
                filters.status.includes(item.status)
            );
        }

        if (filters.dateRange.start || filters.dateRange.end) {
            filtered = filtered.filter((item: any) => {
                const itemDate = moment(item.due_date);
                const startDate = filters.dateRange.start ? moment(filters.dateRange.start).startOf('day') : null;
                const endDate = filters.dateRange.end ? moment(filters.dateRange.end).endOf('day') : null;

                if (startDate && endDate) {
                    return itemDate.isBetween(startDate, endDate, 'day', '[]');
                } else if (startDate) {
                    return itemDate.isSameOrAfter(startDate, 'day');
                } else if (endDate) {
                    return itemDate.isSameOrBefore(endDate, 'day');
                }
                return true;
            });
        }
        
        if (tableFilters.clientName) {
            filtered = filtered.filter((item: any) => 
                item.order?.customer?.name === tableFilters.clientName
            );
        }
        
        if (tableFilters.status.length > 0) {
            filtered = filtered.filter((item: any) => 
                tableFilters.status.includes(item.status)
            );
        }
        
        return filtered;
    }, [incomes?.data, filters, tableFilters]);

    const filteredClientSummary = useMemo(() => {
        let filtered = clientSummary;

        if (filters.clientName) {
            filtered = filtered.filter(client => 
                client.name.toLowerCase().includes(filters.clientName.toLowerCase())
            );
        }

        if (filters.status.length > 0 || filters.dateRange.start || filters.dateRange.end) {
            filtered = filtered.filter(client => {
                const filteredItems = client.items.filter((item: IncomeItem) => {
                    if (filters.status.length > 0 && !filters.status.includes(item.status)) {
                        return false;
                    }

                    if (filters.dateRange.start || filters.dateRange.end) {
                        const itemDate = moment(item.due_date);
                        const startDate = filters.dateRange.start ? moment(filters.dateRange.start).startOf('day') : null;
                        const endDate = filters.dateRange.end ? moment(filters.dateRange.end).endOf('day') : null;

                        if (startDate && endDate) {
                            if (!itemDate.isBetween(startDate, endDate, 'day', '[]')) {
                                return false;
                            }
                        } else if (startDate) {
                            if (!itemDate.isSameOrAfter(startDate, 'day')) {
                                return false;
                            }
                        } else if (endDate) {
                            if (!itemDate.isSameOrBefore(endDate, 'day')) {
                                return false;
                            }
                        }
                    }

                    return true;
                });

                return filteredItems.length > 0;
            });

            filtered = filtered.map(client => {
                const filteredItems = client.items.filter((item: IncomeItem) => {
                    if (filters.status.length > 0 && !filters.status.includes(item.status)) {
                        return false;
                    }

                    if (filters.dateRange.start || filters.dateRange.end) {
                        const itemDate = moment(item.due_date);
                        const startDate = filters.dateRange.start ? moment(filters.dateRange.start).startOf('day') : null;
                        const endDate = filters.dateRange.end ? moment(filters.dateRange.end).endOf('day') : null;

                        if (startDate && endDate) {
                            if (!itemDate.isBetween(startDate, endDate, 'day', '[]')) {
                                return false;
                            }
                        } else if (startDate) {
                            if (!itemDate.isSameOrAfter(startDate, 'day')) {
                                return false;
                            }
                        } else if (endDate) {
                            if (!itemDate.isSameOrBefore(endDate, 'day')) {
                                return false;
                            }
                        }
                    }

                    return true;
                });

                const total = filteredItems.reduce((sum, item) => sum + Number(item.amount), 0);
                const pending = filteredItems.reduce((sum, item) => 
                    item.status === 'PENDING' ? sum + Number(item.amount) : sum, 0);
                const late = filteredItems.reduce((sum, item) => 
                    item.status === 'LATE' ? sum + Number(item.amount) : sum, 0);

                return {
                    ...client,
                    items: filteredItems,
                    total,
                    pending,
                    late
                };
            });
        }

        return filtered;
    }, [clientSummary, filters]);

    const toggleClientExpansion = (clientName: string) => {
        setExpandedClients(prev => ({
            ...prev,
            [clientName]: !prev[clientName]
        }));
    };

    const hasActiveFilters = useMemo(() => {
        return filters.clientName !== '' || 
               filters.status.length > 0 || 
               filters.dateRange.start !== null || 
               filters.dateRange.end !== null;
    }, [filters]);

    const selectedIncomes = useMemo(() => {
        return rowSelectionModel.map(item => {
            return (incomes.data || []).find((income: any) => income.id === item)
        })
    }, [rowSelectionModel])
    console.log(selectedIncomes)
    return (
        <Grid container justifyContent="center" padding={3} gap={3} >
            {confirmModalAction.current && confirmModalVisible && (
                <Modal
                    title="Atenção!"
                    open={confirmModalVisible}
                    onConfirm={confirmModalAction.current}
                    confirmLabel="Confirmar"
                    onClose={() => {
                        setConfirmModalVisibility.setFalse()
                        confirmModalAction.current = null
                    }}
                >
                    <div>
                        Esta ação é irreversível e não pode ser desfeita.<br/>
                        Você tem certeza que deseja <span style={{fontWeight: 'bold'}}>CONFIRMAR</span> este(s) recebimento(s)?
                    </div>
                </Modal>)}
            {(incomes.loading) ? (
                <div style={{
                    display: 'flex',
                    flex: '1',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    <Loader />
                </div>
            ) : (
            <Fragment> 
                <Grid xs={12} sm={10} container justifyContent="flex-end">
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={(_, newMode) => newMode && setViewMode(newMode)}
                        size="small"
                    >
                        <ToggleButton value="timeline">
                            <SwapHoriz sx={{ mr: 1 }} />
                            Por Data
                        </ToggleButton>
                        <ToggleButton value="clients">
                            <SwapHoriz sx={{ mr: 1 }} />
                            Por Cliente
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Grid>

                {viewMode === 'timeline' ? (
                    <Grid xs={12} sm={10}> 
                        <Alert 
                            icon={false}          
                            severity="warning"
                        >
                            <strong className='text-lg'>Próximos Recebimentos:</strong>
                            {dash.some(item => item.amount > 0) ? (
                                <ul
                                    style={{
                                        columnCount: 3,
                                        width: '50vw',
                                        marginTop: 10
                                    }}
                                >
                                    {
                                        dash.map(item => {
                                            return (
                                                <li>
                                                    <strong>{item.due_date}</strong>: {formatMoney(item.amount)}
                                                </li>
                                            )
                                        })
                                    }
                                </ul>
                            ) : (
                                <Typography sx={{ mt: 2 }}>Nenhum recebimento encontrado para o período.</Typography>
                            )}
                        </Alert>
                    </Grid>
                ) : (
                    <Grid xs={12} sm={10}>
                        <Alert icon={false} severity="warning" sx={{ position: 'relative' }}>
                            <Grid container justifyContent="space-between" alignItems="center" marginTop={2}>
                                <strong className='text-lg'>Resumo por Cliente:</strong>
                                <Box sx={{ mt: 2, mb: 2 }} position={'absolute'} right={20} display="flex" gap={1}>
                                    {hasActiveFilters && (
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            onClick={handleClearFilters}
                                        >
                                            Limpar Filtros
                                        </Button>
                                    )}
                                    <Button
                                        variant="outlined"
                                        startIcon={<FilterAlt />}
                                        onClick={handleOpenFilterModal}
                                    >
                                        Filtrar
                                    </Button>
                                </Box>
                            </Grid>
                            {filteredClientSummary.length > 0 ? (
                                <Grid container spacing={2} marginTop={1}>
                                    {filteredClientSummary.map((client) => (
                                        <Grid item xs={12} md={6} lg={4} key={client.name} className='min-w-[300px] ' >
                                            <div className='bg-neutral-50 px-3 py-2 rounded-md shadow-sm border border-neutral-200'>
                                                <Box sx={{ display: 'flex', alignItems: 'center', position:'relative'}}>
                                                    <Typography variant="h6"  color="black">{client.name}</Typography>
                                                        <ExpandMore 
                                                            fontSize='small' 
                                                            onClick={() => toggleClientExpansion(client.name)}
                                                            className='absolute right-2 cursor-pointer'
                                                            sx={{ color: 'black' }} 
                                                        />
                                                </Box>
                                                
                                                <Stack spacing={0}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <AttachMoney sx={{ color: 'green' }}  fontSize='small'/>
                                                            <Typography 
                                                                fontWeight={600} 
                                                                className='text-green-900 cursor-pointer hover:underline'
                                                                onClick={() => handleTotalClick(client.name)}
                                                            >
                                                                Total:
                                                            </Typography>
                                                        </Box>
                                                        <Typography fontWeight={600} className='text-green-900'>{formatMoney(client.total)}</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <HourglassEmpty sx={{ color: '#854d0e' }} fontSize='small'/>
                                                            <Typography 
                                                                className='text-yellow-900 cursor-pointer hover:underline' 
                                                                fontWeight={600}
                                                                onClick={() => handleStatusClick(client.name, 'PENDING')}
                                                            >
                                                                Pendente:
                                                            </Typography>
                                                        </Box>
                                                        <Typography className='text-yellow-900' fontWeight={600}>{formatMoney(client.pending)}</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <ErrorOutline sx={{ color: '#991b1b' }}  fontSize='small'/>
                                                            <Typography 
                                                                className='text-red-900 cursor-pointer hover:underline' 
                                                                fontWeight={600}
                                                                onClick={() => handleStatusClick(client.name, 'LATE')}
                                                            >
                                                                Atrasado:
                                                            </Typography>
                                                        </Box>
                                                        <Typography className='text-red-900' fontWeight={600}>{formatMoney(client.late)}</Typography>
                                                    </Box>
                                                </Stack>

                                                <Collapse in={expandedClients[client.name]}>
                                                    <Divider sx={{ my: 2 }} />
                                                    <Typography variant="subtitle2" color="black" gutterBottom>Detalhes:</Typography>
                                                    <Stack spacing={1}>
                                                        {client.items.map((item: any) => (
                                                            <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <Typography variant="body2" color="black">
                                                                    {formatDate(item.due_date)}: {formatMoney(item.amount)}
                                                                </Typography>
                                                                <Chip 
                                                                    size="small"
                                                                    label={getPaymentStatusLabel(item.status)}
                                                                    sx={{ color: 'black' }}
                                                                />
                                                            </Box>
                                                        ))}
                                                    </Stack>
                                                </Collapse>
                                            </div>
                                        </Grid>
                                    ))}
                                </Grid>
                            ) : (
                                <Typography sx={{ mt: 2, mb: 2 }}>Nenhum cliente encontrado com os filtros aplicados.</Typography>
                            )}
                        </Alert>
                    </Grid>
                )}

                {rowSelectionModel.length ? (<Grid item xs={12} sm={10} textAlign="left">
                    <Button
                        onClick={() => {
                            const text = composeIncomeSummary(
                                selectedIncomes.map((item) => {
                                    return {
                                        sell_price: item.amount,
                                        reservation_code: item.order?.reservation_code,
                                        created_at: item.order?.created_at
                                    }
                                }),
                                selectedIncomes.map(item => item.order?.customer?.name || '')
                            )

                            navigator.clipboard.writeText(text)

                            enqueueSnackbar('Resumo copiado!', {variant: 'success'})
                        }}
                    >
                        Gerar Fechamento
                    </Button>
                    <Button
                        onClick={() => {
                            confirmModalAction.current = async () => {
                                await callAPI({
                                    url: `/income/batch`,
                                    method: 'PUT',
                                    data: {
                                        ids: rowSelectionModel,
                                        status: 'PAID'
                                    }
                                })

                                refetchIncomes()
                                setConfirmModalVisibility.setFalse()
                                setRowSelectionModel([])
                            }

                            setConfirmModalVisibility.setTrue()
                        }}
                    >
                        Confirmar Pagamento
                    </Button>
                </Grid>) : null}
                <Grid item xs={12} sm={10}>
                    <Paper elevation={1}>
                    <Table 
                        rowSelection={true}
                        checkboxSelection
                        disableRowSelectionOnClick
                        onRowSelectionModelChange={(newRowSelectionModel) => {
                            setRowSelectionModel(newRowSelectionModel);
                        }}
                        rowSelectionModel={rowSelectionModel}
                        rowHeight={65}
                        initialState={{
                            filter: {
                                filterModel: {
                                    items: [
                                        { 
                                            field: 'status',
                                            operator: 'in',
                                            value: tableFilters.status.length > 0 ? tableFilters.status : ['PENDING', 'LATE']
                                        }
                                    ]
                                }
                            }
                        }}
                        columns={[
                            { field: 'id', headerName: 'ID', width: 100 },
                            { field: 'created_at', type:'dateTime', headerName: 'Data', width: 200, valueFormatter: ({value}) => {
                                return moment(value).format('DD/MM/YYYY HH:mm:ss')
                            } },
                            { field: 'due_date',type:'date' ,headerName: 'Data do pagamento', width: 200, valueFormatter: ({value}) => {
                                return moment(value).format('DD/MM/YYYY HH:mm:ss')
                            }
                            ,
                             renderCell: ({value, row}) => {
                                return (
                                    <DatePicker 
                                        disabled={row.status === 'PAID' || row.status === 'CANCELLED'}
                                        onChange={async (newValue) => {
                                            const {status} = await  updateIncome({
                                                url: `/income/${row.id}`,
                                                method: 'PUT',
                                                data: {
                                                    due_date: newValue
                                                }
                                            })

                                            if (status === 204) {
                                                enqueueSnackbar('Data de vencimento atualizada com sucesso!', {
                                                    variant: 'success'
                                                })
                                            }
                                        }}
                                        value={moment(value)}
                                    />
                                )
                            } },
                            { field: 'order_id', headerName: 'Emissão', width: 100, type: 'string', renderCell: ({value}) => {
                                return (
                                    <Link href={`/admin/emissoes/${value}`}>
                                        # {value}
                                    </Link>
                                )
                            }},
                            { 
                                field: 'order.reservation_code', 
                                headerName: 'Localizador', 
                                width: 150, 
                                valueGetter: (params) => params.row.order?.reservation_code || ''
                            },          
                            { 
                                field: 'order.airline', 
                                headerName: 'Companhia Aérea', 
                                width: 200, 
                                valueGetter: (params) => params.row.order?.airline || ''
                            },     
                            { field: 'amount', headerName: 'Valor', width: 120, valueFormatter: ({value }) => {
                                return formatMoney(value)
                            }},
                            { 
                                field: 'order.customer.name', 
                                headerName: 'Cliente', 
                                width: 200, 
                                valueGetter: (params) => params.row.order?.customer.name || ''
                            },     
                            {
                                field: 'status', headerName: 'Status', width: 120, 
                                valueFormatter: ({value}) => {
                                    return getPaymentStatusLabel(value)
                                },
                                renderCell: ({ formattedValue, value }) => {
                                    return (
                                        <Chip color={getPaymentStatusColor(value)} label={formattedValue} />
                                    )
                                },
                                filterOperators: statusSelectorOperator
                            },
                            {
                                field: 'actions', 
                                type: 'actions',
                                getActions: (params: GridRowParams<any>) => {
                                    return [
                                        <GridActionsCellItem 
                                            icon={<Close />} 
                                            onClick={() => handleActionClick('CANCELLED', params.row.id as number)} 
                                            label="Marcar como cancelado"
                                            disabled={params.row.status === 'PAID' || params.row.status === 'CANCELLED'}
                                        />,
                                        <GridActionsCellItem 
                                            icon={<Check />} 
                                            onClick={() => handleActionClick('PAID', params.row.id as number)} 
                                            label="Marcar como pago" 
                                            disabled={params.row.status === 'PAID' || params.row.status === 'CANCELLED'}
                                        />
                                    ];
                                }, 
                            }
                        ]}
                        rows={filteredTableData.map((item: { order: { bid: { user: { name: string }}}}) => ({
                            ...item,
                            supplier_name: item?.order?.bid?.user?.name
                        }))}
                    />
                    </Paper>
                </Grid>
            </Fragment>
            )}
              <Modal 
                open={modalVisibility} 
                onClose={modalVisibilityControls.setFalse} 
                confirmLabel='Confirmar'
                onConfirm={confirmAction}
                title='Confirmação'
                >
                <Grid container direction="column" alignItems="center" spacing={2}>
                    <Grid item>
                        <strong>
                            Tem certeza que deseja {pendingAction === "CANCELLED" ? "cancelar" : "marcar como pago"} este recebimento?
                        </strong>
                    </Grid>
               
                </Grid>
            </Modal>

            <Modal
                open={filterModalVisibility}
                onClose={filterModalVisibilityControls.setFalse}
                title="Filtrar Clientes"
                confirmLabel="Aplicar Filtros"
                onConfirm={handleApplyFilters}
            >
                <Grid container spacing={3} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Nome do Cliente"
                            value={tempFilters.clientName}
                            onChange={(e) => handleFilterChange('clientName', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <MultipleAutocomplete
                            variant="standard"
                            label="Status"
                            options={statusOptions}
                            value={statusOptions.filter(option => tempFilters.status.includes(option.value))}
                            onChange={(value) => {
                                handleFilterChange('status', (value as unknown as MultipleAutocompleteOption[]).map(item => item.value));
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>Período</Typography>
                        <DateRangePicker
                            startDate={tempFilters.dateRange.start?.toDate() || new Date()}
                            endDate={tempFilters.dateRange.end?.toDate() || new Date()}
                            onChange={(newValue) => handleFilterChange('dateRange', {
                                start: moment(newValue.startDate),
                                end: moment(newValue.endDate)
                            })}
                        />
                    </Grid>
                </Grid>
            </Modal>
        </Grid>
    )
}